import { z } from 'zod'

/**
 * Formula profile — Part Three D9, register §8.
 *
 * Formula fields use a restricted parser with the adopted operations only
 * (`+ - * /`, parentheses, decimal constants, numeric field references, `SUM`, `MIN`,
 * `MAX`, `ROUND`) and never `eval` or JavaScript. Arithmetic is decimal and consistent
 * under a versioned rounding policy; an empty input is unresolved, not silently zero;
 * cycles, missing references, invalid types and division by zero are rejected;
 * computed outputs are read-only. Dependencies are limited to the same signer's fields
 * plus immutable sender-prefilled values — a cross-signer dependency is unrepresentable
 * because the dependency kind enum has no such member.
 *
 * The rounding/decimal profile values themselves are an open freeze (register §10) and
 * enter the register as a `GATE` row; here only the profile *versions* are required.
 */

export const FORMULA_PROFILE_VERSION = 1 as const

export const FormulaBinaryOperator = z.enum(['ADD', 'SUBTRACT', 'MULTIPLY', 'DIVIDE'])
export type FormulaBinaryOperator = z.infer<typeof FormulaBinaryOperator>

export const FormulaFunction = z.enum(['SUM', 'MIN', 'MAX', 'ROUND'])
export type FormulaFunction = z.infer<typeof FormulaFunction>

/**
 * Allowed dependency kinds. Same signer's fields and immutable sender-prefilled values
 * only: `CROSS_SIGNER_FIELD` is not a member, so it cannot be declared (D9).
 */
export const FormulaDependencyKind = z.enum(['SAME_SIGNER_FIELD', 'SENDER_PREFILLED_IMMUTABLE'])
export type FormulaDependencyKind = z.infer<typeof FormulaDependencyKind>

export const FormulaDependency = z
  .object({
    kind: FormulaDependencyKind,
    field_id: z.string().min(1).max(64),
  })
  .strict()
export type FormulaDependency = z.infer<typeof FormulaDependency>

/** Every binary operator of the adopted set must be declared exactly once. */
const ADOPTED_BINARY_OPERATORS: readonly FormulaBinaryOperator[] = [
  'ADD',
  'SUBTRACT',
  'MULTIPLY',
  'DIVIDE',
]

/** The adopted function set, complete by construction. */
const ADOPTED_FORMULA_FUNCTIONS: readonly FormulaFunction[] = ['SUM', 'MIN', 'MAX', 'ROUND']

export const FormulaProfile = z
  .object({
    profile_version: z.literal(FORMULA_PROFILE_VERSION),
    binary_operators: z.array(FormulaBinaryOperator),
    allow_parentheses: z.literal(true),
    allow_decimal_constants: z.literal(true),
    allow_field_references: z.literal(true),
    functions: z.array(FormulaFunction),
    /** Versions of the frozen decimal and rounding policies (register §10 freeze). */
    decimal_policy_version: z.number().int().positive(),
    rounding_policy_version: z.number().int().positive(),
    dependency_rule: z
      .object({
        allowed_kinds: z.array(FormulaDependencyKind).min(1),
        cross_signer: z.literal('EXCLUDED'),
      })
      .strict(),
    empty_input: z.literal('UNRESOLVED'),
    on_error: z.literal('REJECT'),
    read_only_outputs: z.literal(true),
    evidence: z
      .object({
        store_expression: z.literal(true),
        store_engine_version: z.literal(true),
        store_dependencies: z.literal(true),
        store_result: z.literal(true),
        recompute_at_authorized_verification: z.literal(true),
      })
      .strict(),
  })
  .strict()
  .superRefine((profile, ctx) => {
    for (const operator of ADOPTED_BINARY_OPERATORS) {
      if (!profile.binary_operators.includes(operator)) {
        ctx.addIssue({
          code: 'custom',
          message: `the adopted operator set requires ${operator}`,
          path: ['binary_operators'],
        })
      }
    }
    for (const fn of ADOPTED_FORMULA_FUNCTIONS) {
      if (!profile.functions.includes(fn)) {
        ctx.addIssue({
          code: 'custom',
          message: `the adopted function set requires ${fn}`,
          path: ['functions'],
        })
      }
    }
  })
export type FormulaProfile = z.infer<typeof FormulaProfile>

/**
 * A formula field's declaration and its evidence-bearing result.
 * Composition behaviour (`formula`, `anchor` matching) is M1/M3 work; M0 freezes the
 * shape only.
 */
export const Formula = z
  .object({
    field_id: z.string().min(1).max(64),
    expression: z.string().min(1),
    profile_version: z.literal(FORMULA_PROFILE_VERSION),
    dependencies: z.array(FormulaDependency).min(1),
    input_state: z.enum(['UNRESOLVED', 'COMPUTED']),
    /** `null` while any input is empty — an empty input is never silently zero. */
    result: z.number().nullable(),
    engine_version: z.number().int().positive(),
    read_only: z.literal(true),
  })
  .strict()
  .superRefine((formula, ctx) => {
    if (formula.input_state === 'UNRESOLVED' && formula.result !== null) {
      ctx.addIssue({
        code: 'custom',
        message: 'an unresolved formula has no result — empty is not zero',
        path: ['result'],
      })
    }
    if (formula.input_state === 'COMPUTED' && formula.result === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'a computed formula carries its result',
        path: ['result'],
      })
    }
  })
export type Formula = z.infer<typeof Formula>
